import { createContext, useContext, useState, useEffect } from "react";

const TRANSLATIONS = {
  en: {
    appName: "SusStyle AI",
    tagline: "Your private AI doctor — every question, answered with care",
    subhero: "Get evidence-based medical guidance in your language, 24/7. Backed by WHO, CDC, NIH and peer-reviewed research.",
    getStarted: "Start consultation",
    signIn: "Sign in",
    signUp: "Create account",
    signOut: "Sign out",
    consultation: "Consultation",
    profile: "Health profile",
    history: "History",
    admin: "Admin",
    dashboard: "Dashboard",
    askPlaceholder: "Describe your symptoms or ask a medical question...",
    send: "Send",
    newChat: "New consultation",
    model: "Model",
    language: "Language",
    voice: "Voice",
    listen: "Listen",
    record: "Tap to record",
    stopRecord: "Stop & transcribe",
    sources: "Sources",
    disclaimer: "Educational only — not a substitute for professional medical care. In an emergency, call your local emergency services.",
    welcomeBack: "Welcome back",
    email: "Email",
    password: "Password",
    fullName: "Full name",
    confirmPassword: "Confirm password",
    forgotPassword: "Forgot your password?",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    healthProfile: "Health profile",
    healthProfileDesc: "Your details help the AI give safer, more personalized advice. Always private.",
    save: "Save",
    saved: "Saved",
    age: "Age",
    sex: "Sex",
    height: "Height (cm)",
    weight: "Weight (kg)",
    bloodType: "Blood type",
    allergies: "Allergies",
    chronicConditions: "Chronic conditions",
    medications: "Current medications",
    familyHistory: "Family history",
    smoking: "Smoking",
    alcohol: "Alcohol",
    notes: "Notes",
    commaSeparated: "Comma-separated",
    delete: "Delete",
    confirm: "Confirm",
    cancel: "Cancel",
    you: "You",
    assistant: "SusStyle AI",
    typing: "Thinking...",
    chatHistoryEmpty: "No consultations yet — start your first one.",
    landingFeatureA: "Multi-model AI",
    landingFeatureADesc: "Switch between GPT-5.2, Claude Sonnet 4.5 and Gemini 3 Pro for different perspectives.",
    landingFeatureB: "Voice-first care",
    landingFeatureBDesc: "Speak naturally; the AI listens, transcribes and replies aloud in 15+ languages.",
    landingFeatureC: "Always cited",
    landingFeatureCDesc: "Every clinical claim is backed by WHO, CDC, NIH, FDA, PubMed or peer-reviewed sources.",
    landingFeatureD: "Private & secure",
    landingFeatureDDesc: "Your health profile and chats stay encrypted and never sold.",
  },
  es: {
    appName: "SusStyle AI",
    tagline: "Tu médico privado con IA — cada pregunta, respondida con cuidado",
    subhero: "Recibe orientación médica basada en evidencia en tu idioma, 24/7. Respaldada por OMS, CDC, NIH e investigación revisada por pares.",
    getStarted: "Iniciar consulta",
    signIn: "Iniciar sesión",
    signUp: "Crear cuenta",
    signOut: "Cerrar sesión",
    consultation: "Consulta",
    profile: "Perfil de salud",
    history: "Historial",
    admin: "Admin",
    dashboard: "Panel",
    askPlaceholder: "Describe tus síntomas o haz una pregunta médica...",
    send: "Enviar",
    newChat: "Nueva consulta",
    model: "Modelo",
    language: "Idioma",
    voice: "Voz",
    listen: "Escuchar",
    record: "Toca para grabar",
    stopRecord: "Detener y transcribir",
    sources: "Fuentes",
    disclaimer: "Solo educativo — no sustituye la atención médica profesional. En emergencia, llama a los servicios de emergencia.",
    welcomeBack: "Bienvenido de nuevo",
    email: "Correo",
    password: "Contraseña",
    fullName: "Nombre completo",
    confirmPassword: "Confirmar contraseña",
    noAccount: "¿No tienes cuenta?",
    haveAccount: "¿Ya tienes cuenta?",
    healthProfile: "Perfil de salud",
    healthProfileDesc: "Tus datos ayudan a la IA a dar consejos más seguros y personalizados. Siempre privados.",
    save: "Guardar",
    saved: "Guardado",
    age: "Edad",
    sex: "Sexo",
    height: "Altura (cm)",
    weight: "Peso (kg)",
    bloodType: "Tipo de sangre",
    allergies: "Alergias",
    chronicConditions: "Condiciones crónicas",
    medications: "Medicamentos actuales",
    familyHistory: "Historia familiar",
    smoking: "Tabaco",
    alcohol: "Alcohol",
    notes: "Notas",
    commaSeparated: "Separados por coma",
    delete: "Eliminar",
    confirm: "Confirmar",
    cancel: "Cancelar",
    you: "Tú",
    assistant: "SusStyle AI",
    typing: "Pensando...",
    chatHistoryEmpty: "Aún no hay consultas — inicia la primera.",
    landingFeatureA: "IA multi-modelo",
    landingFeatureADesc: "Cambia entre GPT-5.2, Claude Sonnet 4.5 y Gemini 3 Pro para distintas perspectivas.",
    landingFeatureB: "Atención por voz",
    landingFeatureBDesc: "Habla con naturalidad; la IA escucha, transcribe y responde en voz en más de 15 idiomas.",
    landingFeatureC: "Siempre con fuentes",
    landingFeatureCDesc: "Cada afirmación clínica está respaldada por OMS, CDC, NIH, FDA, PubMed o fuentes revisadas.",
    landingFeatureD: "Privado y seguro",
    landingFeatureDDesc: "Tu perfil y conversaciones permanecen cifradas y nunca se venden.",
  },
};

// Fallback for any unsupported UI language → English copy
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("susstyle_lang") || "en");
  useEffect(() => { localStorage.setItem("susstyle_lang", lang); }, [lang]);
  const t = (key) => (TRANSLATIONS[lang] && TRANSLATIONS[lang][key]) || TRANSLATIONS.en[key] || key;
  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);

export const UI_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "es", name: "Español" },
];
